/**
 * The meeting page's layout decisions, kept out of the component so they can
 * be reasoned about — and tested — without a browser.
 *
 * The page has three tabs and, on a wide screen, a two-pane Minutes view with
 * the transcript beside the editor. Where a citation click should land depends
 * on both of those, and getting it wrong is how a reviewer loses their place:
 * on a narrow screen the cited line is on another tab, and jumping to it has
 * to be undoable.
 */

export const MEETING_TABS = ['minutes', 'transcript', 'diagnostics'] as const;
export type MeetingTab = (typeof MEETING_TABS)[number];

/**
 * Below this the transcript cannot sit beside the editor and still be read;
 * two 500-px columns are two unusable columns. Matches the CSS media query.
 */
export const TWO_PANE_MIN_PX = 1200;

export function isTwoPaneWidth(width: number): boolean {
  return width >= TWO_PANE_MIN_PX;
}

/** The media query the page listens to, so the CSS and the JS cannot drift. */
export const TWO_PANE_QUERY = `(min-width: ${TWO_PANE_MIN_PX}px)`;

export function visibleTabs(isAdmin: boolean): MeetingTab[] {
  // Diagnostics is an operator tool; it is not a tab for everybody else.
  return isAdmin ? [...MEETING_TABS] : ['minutes', 'transcript'];
}

/**
 * Minutes first when there are minutes to read — that is what the page is for
 * once a MOM exists. Before that the transcript is the only thing on the page,
 * and opening on an empty Minutes tab would hide it.
 */
export function defaultTab({ hasMom }: { hasMom: boolean }): MeetingTab {
  return hasMom ? 'minutes' : 'transcript';
}

export type CiteTarget = 'side-pane' | 'transcript-tab';

/**
 * Where a citation chip in the minutes should scroll to.
 *
 * With both panes on screen the transcript is already visible beside the
 * editor, so the jump happens in place and the reviewer never leaves the
 * minutes. Otherwise the page has to switch tabs, and the caller owes the
 * reviewer a way back.
 */
export function citeTarget({
  twoPane,
  activeTab,
}: {
  twoPane: boolean;
  activeTab: MeetingTab;
}): CiteTarget {
  return twoPane && activeTab === 'minutes' ? 'side-pane' : 'transcript-tab';
}

/** The "Back to minutes" button only belongs where a citation sent you away. */
export function showBackToMinutes({
  activeTab,
  cameFromCitation,
}: {
  activeTab: MeetingTab;
  cameFromCitation: boolean;
}): boolean {
  return activeTab === 'transcript' && cameFromCitation;
}
